import {
  PrtcpntPrvdrAssociationModel,
} from '../../../../shared/interfaces/participant-association.model';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Component,
  Input,
  OnInit,
  SimpleChanges,
  OnChanges,
  signal,
  WritableSignal,
  TemplateRef,
  ViewChild,
  inject,
  OnDestroy,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { differenceInCalendarDays } from 'date-fns';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { SectionsService } from '../../../../services/sections.service';
import { ButtonComponent, DatepickerComponent, DropdownSelectComponent } from '@ppl/ui';
import { ListTableComponent } from '@ppl/ui';
import {
  ASSOCIATION_TYPE_UI_MAPPINGS,
  ASSOCIATIONS_ADD_BUTTON,
  ENROLLMENT_TYPE,
  RADIO_YES_NO_OPTIONS,
  SINGLE_INSTANCE_EMPLOYMENT_TASKS,
  STATIC_DATA,
  STATIC_JSON_DATA_PATH,
} from '../../../../shared/constants/static-data.constants';
import { ERROR_MESSAGES } from '../../../../shared/constants/error-message.constants';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { LoggerService } from '../../../../shared/services/logger.service';
import { BaseComponent } from '../../../../shared/utils/base.component';
import { AssociationDetailsAddressComponent } from '../association-details-address/association-details-address.component';
import { EnrollmentDataService } from '../../../../services/enrollment-data.service';
import { performValidation } from '../../../../shared/utils/validation.util';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';
import { ProviderSearchAndSelectComponent } from '../../provider-search-and-select/provider-search-and-select.component';
import { AddAssociationFormComponent } from '../../add-association-form/add-association-form.component';
import { SearchProvidersDetailsItem } from '../../../../shared/interfaces/provider.model';
import { TableRowAction } from '../../../../shared/interfaces/list.interface';
import { AuthorizationUtility } from '../../../../shared/authorization/auth.utility';
import { DialogConfig, DialogService } from '../../../../shared/services/dialog.service';
import { ReferralDataService } from '../../../../services/referral-data.service';
import { UserService } from '../../../../services/user.service';

enum Mode {
  Add = 'add',
  Edit = 'edit',
  Read = 'read',
  Search = 'search',
}
@Component({
  selector: 'ppl-association-details',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NzDatePickerModule,
    CommonModule,
    NgZorroModule,
    NzAlertModule,
    ButtonComponent,
    DatepickerComponent,
    DropdownSelectComponent,
    ListTableComponent,
    AssociationDetailsAddressComponent,
    ProviderSearchAndSelectComponent,
    AddAssociationFormComponent
  ],
  templateUrl: './association-details.component.html',
  styleUrl: './association-details.component.css',
})
export class AssociationDetailsComponent
  extends BaseComponent
  implements OnInit, OnChanges, OnDestroy {
  radioOptions = RADIO_YES_NO_OPTIONS;
  mode: Mode = Mode.Read;
  sectionDataPath = '';
  steps: any;
  entry = 'base';
  infoForm!: FormGroup;
  disabledDate!: (current: Date) => boolean;
  ssnVisible = false;
  gender: any[] = [];
  statesList: any;
  validationErrorsPermanentAdd: any = {};
  participantRegisterMode: any[] = [];
  addBtnLabel = '';
  program: any;
  formReadonly = false;
  isConsumerAbove18 = false;

  @Input() sectionName = '';
  @Input() gridData: any;
  @Input() gridCols: any;
  @Input() pageId = '';
  @Input() tabId = '';
  @Input() sectionId = '';
  @Input() consumerData: any;
  @Input() allowMultiple: boolean = true;
  @Input() allAssociations: any = null;

  actions: any = {
    view: true,
  };
  selectedAction: WritableSignal<TableRowAction | null> = signal(null);
  readOnly = false;
  relationshipOptions: any;
  eyeColors: any;
  hairColors: any;
  raceOptions: any;
  taxExemptOptions: any;
  isSectionEditable: boolean = false;
  userDetails: any;

  // manually override the component to make it readonly
  @Input() readonly = false;

  copyOfGridData: any = [
    {
      fname: 'Danny',
      lname: 'Rose',
      email: 'dannyrose@gmail.com',
      contactNumber: '7975453298',
    },
  ];

  selectedValue = null;
  listOfOptions: { value: string; text: string }[] = [];
  nzFilterOptions = (): boolean => true;
  fullAddress = '';
  licences: any[] = [];
  services: any[] = [];
  permanentAddressErrors: any = {};
  selectedProviderType: any;
  selectedPartiProviderAssocId: any;

  selectedProviderToAdd: WritableSignal<SearchProvidersDetailsItem | null> = signal(null);
  @ViewChild('confirmationDialog') confirmationDialog!: TemplateRef<any>;
  @ViewChild('deleteConfirmationDialog') deleteConfirmationDialog!: TemplateRef<any>;
  @ViewChild('employerRestrictionDialog') employerRestrictionDialog!: TemplateRef<any>;
  private dialogSvc = inject(DialogService);

  disassociationForm!: FormGroup;
  disassociationFormErrors: any = {};
  showDisassociationReason = false;
  disassociationReasonList: any[] = [];
  selectedDisassociationData: any = null;
  disassociationApiError: string | null = null;
  restoreRehireApiError: string | null = null;

  disabledFutureDate = (_current: Date): boolean => false;

  taskManagerOptions: any[] = [];
  /**
   * Employment task codes that cannot be picked for the incoming EOR. Held as a field rather than
   * a getter so the bound reference stays stable across change detection.
   */
  disabledTaskManagerCodes: string[] = [];

  get providerType(): string {
    return ASSOCIATION_TYPE_UI_MAPPINGS[this.sectionName] ?? ''
  }

  /** Employer associations that are still in play (a disassociated EOR is not the current one). */
  private get activeEmployerAssociations(): any[] {
    return (this.gridData ?? []).filter((e: any) => e.associationStatus !== 'Disassociated');
  }

  /**
   * Change of EOR is offered only when the participant has a single live Employer of Record
   * and that association is in Paperwork Complete. Requiring a single active association keeps
   * a second change request from being started while one is already in flight.
   */
  get showChangeEorButton(): boolean {
    if (this.sectionName !== STATIC_DATA.ASSOCIATION_EMPLOYER || !this.isSectionEditable) {
      return false;
    }
    const activeAssociations = this.activeEmployerAssociations;
    return (
      activeAssociations.length === 1 &&
      activeAssociations[0].associationStatus === STATIC_DATA.ASSOCIATION_PAPERWORK_COMPLETE_STATUS
    );
  }

  /**
   * Employment task codes that must not be reselected for an incoming EOR. Self, Father and
   * Mother each identify one specific person, so the existing EOR's task manager cannot be used
   * again; the remaining options (Child, Grandchild, Non/Other Relative) can repeat. Only the
   * employer section is restricted - other sections pick from the Relationship lookup, which
   * shares value names with Employment_Tasks and would otherwise be restricted by accident.
   */
  private getTaskManagerCodesToDisable(): string[] {
    if (this.sectionName !== STATIC_DATA.ASSOCIATION_EMPLOYER) {
      return [];
    }
    const relation = this.activeEmployerAssociations[0]?.relationToParticipant;
    if (!relation) {
      return [];
    }
    // The association grid carries either the lookup code or its display name, depending on the API.
    const relationValue = String(relation).trim().toLowerCase();
    const currentOption = this.taskManagerOptions.find(
      (option: any) =>
        String(option.lkpValueCode ?? '').toLowerCase() === relationValue ||
        String(option.lkpValueName ?? '').trim().toLowerCase() === relationValue
    );
    if (!currentOption) {
      return [];
    }
    const currentName = String(currentOption.lkpValueName ?? '').trim().toLowerCase();
    const isSingleInstance = SINGLE_INSTANCE_EMPLOYMENT_TASKS.some((task) =>
      currentName.startsWith(task)
    );
    return isSingleInstance ? [currentOption.lkpValueCode] : [];
  }

  constructor(
    private logger: LoggerService,
    private sectionsService: SectionsService,
    private enrollmentDataService: EnrollmentDataService,
    private dateTimeFormatterService: DateTimeFormatterService,
    private authUtility: AuthorizationUtility,
    private referralDataService: ReferralDataService,
    private userService: UserService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.userDetails = this.userService.getUserDetails();
    this.entry = this.userService.getLeadEntry() ?? '';
    this.program = this.userService.getSelectedProgram();
    this.sectionDataPath =
      STATIC_JSON_DATA_PATH.LEAD_DETAILS_PROVIDERS_SECTION_DATA;

    this.isSectionEditable = this.readonly ? false : this.authUtility.isSectionEditable(this.pageId, this.tabId, 'Associations');
    this.addBtnLabel = ASSOCIATIONS_ADD_BUTTON[this.sectionName];

    this.getSectionData();
    this.createForm();

    this.disassociationReasonList = this.getLookup('Disassociation Reason')
      .sort((a: any, b: any) => a.lkpValueName?.toLowerCase().includes('other') ? 1 : b.lkpValueName?.toLowerCase().includes('other') ? -1 : 0);
    this.disassociationForm = new FormBuilder().group({
      disassociationDate: [null],
      disassociationReason: [null],
    });

    this.statesList = this.lookupService.getLookupValue('STATE');
    this.participantRegisterMode = this.getLookup('Participant_Register_Mode');
    this.relationshipOptions = this.getLookup('Relationship');
    this.eyeColors = this.getLookup('Eye Color');
    this.hairColors = this.getLookup('Hair Color');
    this.raceOptions = this.getLookup('Race');
    this.taxExemptOptions = this.getLookup('Tax Exemption');
    this.taskManagerOptions = this.getLookup('Employment_Tasks') ?? [];

    const today = new Date();
    this.disabledDate = (current: Date): boolean => {
      return differenceInCalendarDays(current, today) >= 0;
    };

    this.gender = this.getLookup('Gender');
    this.licences = this.getLookup('License_Type');
    this.services = this.getLookup('Service_Type');
  }

  ngOnChanges(changes: SimpleChanges) {
    try {
      if (changes['sectionName'] && changes['sectionName'].currentValue) {
        this.getSectionData();
      }
    } catch (e) {
      console.log('Error in getting section data and updating latest info', e);
    }
  }

  async getSectionData() {
    try {
      if (this.entry) {
        this.steps = await this.sectionsService.getJsonConfigurationData(
          this.sectionDataPath,
          this.entry,
          this.sectionName
        );

        this.secData = this.steps;
        /* if (this.sectionName === STATIC_DATA.NEW_EMPLOYER_INFO) {
          this.secData = this.steps[STATIC_DATA.NEW_EMPLOYER_INFO];
        } else if (this.sectionName === STATIC_DATA.AUTHORIZED_REPRESENTATIVE) {
          this.secData = this.steps[STATIC_DATA.AUTHORIZED_REPRESENTATIVE];
        } else if (this.sectionName === STATIC_DATA.INDEPENDENT_PROVIDER) {
          this.secData = this.steps[STATIC_DATA.INDEPENDENT_PROVIDER];
        } else if (this.sectionName === STATIC_DATA.NEW_VENDOR_DETAILS) {
          this.secData = this.steps[STATIC_DATA.NEW_VENDOR_DETAILS];
        } else if (
          this.sectionName === STATIC_DATA.INDEPENDENT_CONTRACTOR_DETAILS
        ) {
          this.secData = this.steps[STATIC_DATA.INDEPENDENT_CONTRACTOR_DETAILS];
        } else if (this.sectionName === STATIC_DATA.LEGAL_GUARDIAN) {
          this.secData = this.steps[STATIC_DATA.LEGAL_GUARDIAN];
        } else if (this.sectionName === STATIC_DATA.POWER_OF_ATTORNEY) {
          this.secData = this.steps[STATIC_DATA.POWER_OF_ATTORNEY];
        } */
      }
    } catch (e) {
      this.logger.debug('Error fetching section details data', e);
    }
  }

  checkAge(date: string) {
    const [month, day, year] = date.split('/').map(Number);
    const dob = new Date(year, month - 1, day);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    const dayDiff = today.getDate() - dob.getDate();
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      age--;
    }
    this.isConsumerAbove18 = age >= 18;
  }

  createForm(): void {
    const formElements: any = {
      businessName: new FormControl(''),
      fname: new FormControl(''),
      lname: new FormControl(''),
      dob: new FormControl(null),
      email: new FormControl(''),
      mobile: new FormControl(''),
      legalName: new FormControl(''),
      permanentAddress: new FormGroup({
        leadId: new FormControl(),
        street1: new FormControl(''),
        street2: new FormControl(''),
        city: new FormControl(''),
        state: new FormControl(''),
        zipCode: new FormControl(''),
        countyOrRegion: new FormControl(''),
        psd_code: new FormControl(''),
        mailing: new FormControl(false),
      }),
    };

    if (
      this.sectionName === STATIC_DATA.ASSOCIATION_AUTHORIZED_REPRESENTATIVE
    ) {
      formElements.gender = new FormControl(null);
      formElements.SSN = new FormControl('');
      formElements.relationToParticipant = new FormControl(null);
      formElements.additionalDemographics = new FormControl('');
      formElements.race = new FormControl('');
      formElements.heightFeet = new FormControl('');
      formElements.heightInches = new FormControl('');
      formElements.weight = new FormControl('');
      formElements.eyeColor = new FormControl('');
      formElements.hairColor = new FormControl('');
      formElements.birthPlace = new FormControl('');
      formElements.birthCity = new FormControl('');
      formElements.birthState = new FormControl('');
      formElements.birthCountry = new FormControl('');
      formElements.altPhone = new FormControl('');
      formElements.registerPref = new FormControl(null);
      formElements.consent = new FormControl(false);
    }
    if (this.sectionName === STATIC_DATA.ASSOCIATION_INDEPENDENT_PROVIDER) {
      formElements.mname = new FormControl('');
      formElements.maiden_name = new FormControl('');
      formElements.gender = new FormControl(null);
      formElements.SSN = new FormControl('');
      formElements.relationToParticipant = new FormControl(null);
      formElements.NPI = new FormControl('');
      formElements.altPhone = new FormControl('');
      formElements.registerPref = new FormControl(null);
      formElements.yearsInState = new FormControl('');
      formElements.emergencyContactFirstName = new FormControl('');
      formElements.emergencyContactLastName = new FormControl('');
      formElements.emergencyContactPhone = new FormControl('');
      formElements.profLicences = new FormControl([]);
      formElements.servicesRendered = new FormControl([]);
    }
    if (this.sectionName === STATIC_DATA.ASSOCIATION_EMPLOYER) {
      formElements.taskManager = new FormControl(null);
      formElements.relationToParticipant = new FormControl(null);
      formElements.gender = new FormControl(null);
      formElements.SSN = new FormControl('');
      formElements.EIN = new FormControl('');
      formElements.altPhone = new FormControl('');
      formElements.registerPref = new FormControl(null);
    }
    if (this.sectionName === STATIC_DATA.ASSOCIATION_INDEPENDENT_CONTRACTOR) {
      formElements.mname = new FormControl('');
      formElements.gender = new FormControl(null);
      formElements.SSN = new FormControl('');
      formElements.EIN = new FormControl('');
      formElements.altPhone = new FormControl('');
      formElements.relationToParticipant = new FormControl(null);
      formElements.taxExempt = new FormControl('');
      formElements.nprftDocumentationReceived = new FormControl('');
      formElements.profLicences = new FormControl([]);
      formElements.servicesRendered = new FormControl([]);
    }
    if (this.sectionName === STATIC_DATA.ASSOCIATION_VENDOR) {
      formElements.vendorName = new FormControl();
      formElements.dbaName = new FormControl();
      formElements.taxExempt = new FormControl();
      formElements.nprftDocumentationReceived = new FormControl();
      formElements.SSN = new FormControl('');
      formElements.EIN = new FormControl('');
      formElements.mname = new FormControl();
      formElements.altPhone = new FormControl('');
    }
    if (this.sectionName === STATIC_DATA.ASSOCIATION_INDEPENDENT_FACILITATOR) {
      formElements.vendorName = new FormControl();
      formElements.dbaName = new FormControl();
      formElements.taxExempt = new FormControl();
      formElements.nprftDocumentationReceived = new FormControl();
      formElements.SSN = new FormControl('');
      formElements.EIN = new FormControl('');
      formElements.mname = new FormControl();
      formElements.altPhone = new FormControl('');
      formElements.relationToParticipant = new FormControl(null);
    }

    if (this.sectionName === STATIC_DATA.ASSOCIATION_LEGAL_GUARDIAN) {
      formElements.mname = new FormControl('');
      formElements.gender = new FormControl(null);
      formElements.SSN = new FormControl('');
      formElements.EIN = new FormControl('');
      formElements.altPhone = new FormControl('');
      formElements.registerPref = new FormControl(null);
    }

    if (this.sectionName === STATIC_DATA.ASSOCIATION_POWER_OF_ATTORNEY) {
      formElements.mname = new FormControl('');
      formElements.gender = new FormControl(null);
      formElements.SSN = new FormControl('');
      formElements.EIN = new FormControl('');
      formElements.altPhone = new FormControl('');
      formElements.registerPref = new FormControl(null);
    }

    if (this.sectionName === STATIC_DATA.ASSOCIATION_CONSERVATOR) {
      formElements.mname = new FormControl('');
      formElements.SSN = new FormControl('');
      formElements.conservatorship = new FormControl(null);
    }

    this.infoForm = new FormGroup(formElements);
  }

  getFormGroup(controlName: string): FormGroup {
    return this.infoForm.get(controlName) as FormGroup;
  }

  addAssociation() {
    this.disabledTaskManagerCodes = this.getTaskManagerCodesToDisable();
    this.sectionsService.setEnrollmentType(JSON.stringify([ENROLLMENT_TYPE[this.sectionName]]));
    this.mode = Mode.Edit
  }

  searchAssociation() {
    this.disabledTaskManagerCodes = this.getTaskManagerCodesToDisable();
    this.mode = Mode.Search;
    this.sectionsService.setEnrollmentType(JSON.stringify([ENROLLMENT_TYPE[this.sectionName]]));
  }

  /**
   * Change of EOR runs the same flow as adding an employer. The outgoing association is left
   * untouched here - the backend keeps it in Paperwork Complete until the incoming EOR completes
   * enrollment and is approved, then disassociates it.
   */
  changeEor() {
    this.addAssociation();
  }

  cancelEdit() {
    this.mode = Mode.Read;
    this.infoForm.reset();
    this.validationErrors = {};
    this.permanentAddressErrors = {};
    this.formReadonly = false;
    this.infoForm.enable();
    this.selectedProviderToAdd.set(null);
    this.readOnly = false;
    this.disabledTaskManagerCodes = [];
  }

  save() {
    const formData = this.infoForm.value;
    this.validationErrors = {};

    try {
      const formErrors = performValidation(formData, this.secData, 'Next', undefined, undefined, this.sectionsService.getFlow(), this.sectionsService.getEnrollmentType());
      delete formErrors.city;
      delete formErrors.countyOrRegion;
      delete formErrors.state;
      delete formErrors.street1;
      delete formErrors.zipCode;

      // this.permanentAddressErrors = this.getAddressValidations(
      //   formData.permanentAddress
      // ); //{street1, city, state, zipCode, countyOrRegion};
      if (this.sectionName === 'independent_provider' && this.referralDataService?.demographicData && (this.infoForm.value.relationToParticipant == 'PRSP')) {
        const dob = this.dateTimeFormatterService.formatStringToDate(this.referralDataService?.demographicData?.dob);
        if (this.getAge(dob) < 18) {
          this.validationErrors['relationToParticipant'] = "Parent cannot be a Independent Provider to an underaged participant";
        }
      }
      this.validationErrors = { ...formErrors, ...this.validationErrors };
    } catch (e) {
      console.log('Error in validations', e);
    }

    if (Object.keys(this.validationErrors).length > 0) {
      return;
    } else {
      const payload = this.preperePayload();

      console.log('FINAL DATA', formData);

      this.enrollmentDataService
        .saveParticipantAssociations(payload, true)
        .subscribe((data: any) => {
          console.log('SAVE ASSOC', data);
          this.cancelEdit();
          this.enrollmentDataService.setParticipantAssocChanged(true);
        });
    }
  }

  action(event: any): void {
    this.logger.debug('Action', event);
    if (event.action === 'view') {
      this.mode = Mode.Edit;
      this.selectedAction.set(TableRowAction.View);
      this.readOnly = true;
      this.selectedProviderType = event.data.associationTypeCode;
      this.selectedPartiProviderAssocId = event.data.partiProviderAssocId;
      console.log(event.data);
      this.selectedProviderToAdd.set(event.data);
      this.formReadonly = true;
      this.infoForm.disable();
      // provider_id
      // const payload = {
      //   programCode: this.program.program_code,
      //   providerId: event.data.providerId,
      //   providerType: event.data.associationTypeCode,
      //   partiProviderAssocId: event.data.partiProviderAssocId
      // };
      // this.enrollmentDataService
      //   .fetchParticipantAssociations(payload)
      //   .subscribe((response) => {
      //     console.log('Edit response', response);
      //     const flattenedData = this.flattenResponse(response);
      //     console.log(flattenedData.responsedata);
      //     this.infoForm.patchValue(flattenedData.responsedata);
      //   });
    }
    if (event.action === 'disassociate') {
      if (this.consumerData) {
        this.checkAge(this.consumerData.dob);
      }
      if (
        event.data.associationTypeCode === 'EMP' &&
        event.data.associationStatus === 'Paperwork Complete'
      ) {
        this.openEmployerRestrictionPopup();
      } else {
        this.openConfirmationPopup(event.data);
      }
    }
    if (event.action === 'restoreAssociation') {
      this.restoreAssociation(event.data);
    }
    if (event.action === 'rehireAssociation') {
      this.rehireAssociation(event.data);
    }
  }

  getDRCount() {
    const activeDRs = this.gridData.filter(
      (item: any) => item.associationStatus !== 'Disassociated'
    );
    return activeDRs.length;
  }

  disassociate(data: PrtcpntPrvdrAssociationModel, disassociationDate?: string, disassociationReason?: string, closeFn?: () => void) {
    const payload = {
      statusId: null,
      associationId: Number((data as any).partiProviderAssocId),
      startDate: disassociationDate
        ? this.dateTimeFormatterService.formatToString(this.dateTimeFormatterService.formatStringToDate(disassociationDate))
        : null,
      endDate: null,
      status: 'Disassociated',
      comment: null,
      reason: disassociationReason,
      operation: "Disassociated"
    };

    this.selectedPartiProviderAssocId = (data as any).partiProviderAssocId;
    this.updateAssociationStatus(payload, closeFn);
  }

  restoreAssociation(data: PrtcpntPrvdrAssociationModel) {
    const payload = {
      statusId: null,
      associationId: Number((data as any).partiProviderAssocId),
      startDate: this.dateTimeFormatterService.formatToString(new Date()),
      endDate: null,
      status: 'Restore',
      comment: null,
      reason: null,
      operation: null
    };
    this.selectedPartiProviderAssocId = (data as any).partiProviderAssocId;
    this.updateAssociationStatus(payload, undefined, (msg) => this.restoreRehireApiError = msg, ERROR_MESSAGES.SUCCESS_PARTICIPANT_RESTORE_STATUS_SAVE);
  }

  rehireAssociation(data: PrtcpntPrvdrAssociationModel) {
    const payload = {
      statusId: null,
      associationId: Number((data as any).partiProviderAssocId),
      startDate: this.dateTimeFormatterService.formatToString(new Date()),
      endDate: null,
      status: 'Rehire',
      comment: null,
      reason: null,
      operation: null
    };
    this.selectedPartiProviderAssocId = (data as any).partiProviderAssocId;
    this.updateAssociationStatus(payload, undefined, (msg) => this.restoreRehireApiError = msg, ERROR_MESSAGES.SUCCESS_PARTICIPANT_REHIRE_STATUS_SAVE);
  }

  updateAssociationStatus(payload: any, closeFn?: () => void, onError?: (msg: string | null) => void, successMessage: string = ERROR_MESSAGES.SUCCESS_PARTICIPANT_DISASSOCIATION_STATUS_SAVE) {
    const setError = onError ?? ((msg: string | null) => this.disassociationApiError = msg);
    this.enrollmentDataService
      .createEnrollmentStatus(payload, this.program.program_code, this.selectedPartiProviderAssocId, true, 'Add Status Failed', successMessage)
      .subscribe((response: any) => {
        if (response?.responsedata === 'Add Status Failed' && response?.status === 200) {
          setError(response?.responsemsg?.[0]?.desc ?? 'An error occurred during the status update.');
          return;
        }

        setError(null);
        this.enrollmentDataService.setParticipantAssocChanged(true);
        closeFn?.();
      });
  }

  preperePayload() {
    const selectedId = this.userService.getSelectedLead();
    console.log(this.infoForm.value);
    const payload = {
      participantId: selectedId,
      providerId: 0,
      programCode: this.program.program_code,
      pageType: 'participantDetail',
      infoData: this.generateInfoDataPayload(),
      serviceDetails: this.generateServiceDataPayload(),
      communicationData: this.generateCommunicationDataPayload(),
      contactDetails: this.generateContactDataPayload(),
    };
    console.log('payload', payload);

    return payload;
  }

  generateInfoDataPayload() {
    const providerType = ASSOCIATION_TYPE_UI_MAPPINGS[this.sectionName];

    if (providerType === 'EMP') {
      this.infoForm.patchValue({ relationToParticipant: this.infoForm.get('taskManager')?.value });
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { SSN, EIN, NPI, dob, permanentAddress, ...restInfoData } = this.infoForm.value;

    return {
      ...restInfoData,
      fname: restInfoData.fname.trim(),
      lname: restInfoData.lname.trim(),
      providerType: providerType,
      providerId: 0,
      // applicationStatus: 'In Progress',
      finalApplicantDisposition: 'Enrollment',
      programCode: this.program.program_code,
      dob: this.dateTimeFormatterService.formatDateToString(dob as Date),
      identifiers: this.generateIdentifiers(SSN, EIN, NPI, providerType),
    };
  }

  generateIdentifiers(SSN: string, EIN: string, NPI: string, providerType?: string) {
    const einType = providerType === 'EMP' ? 'EINES' : 'EIN';
    const identifiers = [
      SSN && { type: 'SSN', value: SSN },
      EIN && { type: einType, value: EIN },
      NPI && { type: 'NPI', value: NPI },
    ].filter(Boolean);
    return identifiers;
  }

  generateContactDataPayload() {
    const {
      emergencyContactFirstName,
      emergencyContactLastName,
      emergencyContactPhone,
    } = this.infoForm.value;
    return {
      providerId: 0,
      programCode: this.program.program_code,
      emergencyContactFirstName,
      emergencyContactLastName,
      emergencyContactPhone,
    };
  }

  openConfirmationPopup(data: any) {
    const reasonRequiredTypes = [
      STATIC_DATA.ASSOCIATION_INDEPENDENT_PROVIDER,
      STATIC_DATA.ASSOCIATION_INDEPENDENT_CONTRACTOR,
      STATIC_DATA.ASSOCIATION_VENDOR,
      STATIC_DATA.ASSOCIATION_INDEPENDENT_FACILITATOR
    ];
    this.showDisassociationReason = reasonRequiredTypes.includes(this.sectionName);
    this.selectedDisassociationData = data;
    this.disassociationForm.reset();
    this.disassociationFormErrors = {};

    const dialogConfig: DialogConfig = {
      title: 'Disassociation',
      size: 'large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };

    this.dialogSvc.openDialog(
      this.confirmationDialog,
      dialogConfig,
      () => { },
      () => this.handleCancel()
    );
  }

  onDisassociate(closeFn: () => void) {
    this.disassociationFormErrors = {};
    const { disassociationDate, disassociationReason } = this.disassociationForm.value;

    if (!disassociationDate) {
      this.disassociationFormErrors['disassociationDate'] = 'Please select the disassociation date';
    }
    if (this.showDisassociationReason && !disassociationReason) {
      this.disassociationFormErrors['disassociationReason'] = 'Please select the disassociation reason';
    }

    if (Object.keys(this.disassociationFormErrors).length > 0) {
      return;
    }

    const formattedDate = disassociationDate ? this.dateTimeFormatterService.formatDateToString(disassociationDate) : undefined;
    this.disassociationApiError = null;
    this.disassociate(this.selectedDisassociationData, formattedDate, disassociationReason, closeFn);
  }

  openDeleteConfirmationPopup() {
    const dialogConfig: DialogConfig = {
      title: 'Error',
      okText: 'Ok',
      size: 'medium',
      position: 'top',
      showClose: true,
      showOkButton: true,
      showButtons: false,
    };

    this.dialogSvc.openDialog(
      this.deleteConfirmationDialog,
      dialogConfig,
      () => this.handleCancel(),
      () => this.handleCancel()
    );
  }

  openEmployerRestrictionPopup() {
    const dialogConfig: DialogConfig = {
      title: 'Cannot Disassociate',
      okText: 'Ok',
      size: 'medium',
      position: 'top',
      showClose: true,
      showOkButton: true,
      showButtons: false,
    };

    this.dialogSvc.openDialog(
      this.employerRestrictionDialog,
      dialogConfig,
      () => this.handleCancel(),
      () => this.handleCancel()
    );
  }

  handleCancel() {
    this.disassociationApiError = null;
  }

  generateCommunicationDataPayload() {
    const { permanentAddress, email, altPhone, mobile } = this.infoForm.value;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { leadId, mailing, ...restAddress } = permanentAddress;

    const contactMethods = [
      {
        providerId: 0,
        programCode: this.program.program_code,
        contactMethod: 'email',
        value: email,
      },
      {
        providerId: 0,
        programCode: this.program.program_code,
        contactMethod: 'mobile',
        value: mobile,
      },
    ];

    // Conditionally add altPhone if it is not null or an empty string
    if (altPhone) {
      contactMethods.push({
        providerId: 0,
        programCode: this.program.program_code,
        contactMethod: 'altPhone',
        value: altPhone,
      });
    }

    return {
      permanentAddress: {
        providerId: 0,
        programCode: this.program.program_code,
        mailing: true,
        ...restAddress,
      },
      contactMethods: contactMethods,
    };
  }

  generateServiceDataPayload() {
    const { registerPref, profLicences, servicesRendered } =
      this.infoForm.value;
    const profLicencesMOdified =
      profLicences &&
      profLicences.map((license: string) => ({ licenseType: license }));
    const servicesRenderedModified =
      servicesRendered &&
      servicesRendered.map((service: string) => ({ serviceType: service }));
    return {
      providerId: 0,
      programCode: this.program.program_code,
      profLicences: profLicencesMOdified,
      servicesRendered: servicesRenderedModified,
      registerPref,
    };
  }

  flattenResponse(data: any) {
    const flattened: any = { responsedata: {} };

    if (data.responsedata && data.responsedata.basicInfo) {
      Object.assign(flattened.responsedata, data.responsedata.basicInfo);
    }

    if (data.responsedata && data.responsedata.contactDetails) {
      Object.assign(flattened.responsedata, data.responsedata.contactDetails);
    }
    if (data.responsedata && data.responsedata.contactDetails) {
      const communicationData = data.responsedata.contactDetails || {};
      const contactMethods = communicationData.contactMethods || [];
      const contactValues = contactMethods.reduce((acc: any, item: any) => {
        acc[item.contactMethod] = item.value;
        return acc;
      }, {});

      Object.assign(flattened.responsedata, {
        altPhone: contactValues.altPhone || '',
        mobile: contactValues.mobile || '',
        email: contactValues.email || '',
      });
    }
    if (data.responsedata && data.responsedata.communicationPreferences) {
      Object.assign(
        flattened.responsedata,
        data.responsedata.communicationPreferences
      );
    }
    if (data.responsedata && data.responsedata.serviceDetails) {
      const profLicences: string[] =
        data.responsedata.serviceDetails?.profLicences?.map(
          (entry: any) => entry.licenseType
        ) || [];
      const servicesRendered: string[] =
        data.responsedata.serviceDetails?.servicesRendered?.map(
          (entry: any) => entry.serviceType
        ) || [];

      Object.assign(flattened.responsedata, {
        profLicences,
        servicesRendered,
      });
    }

    const identifiers = data.responsedata.basicInfo?.identifiers || [];
    const ssn =
      identifiers.find((item: any) => item.type === 'SSN')?.value || '';
    const ein =
      identifiers.find((item: any) => item.type === 'EIN')?.value || '';
    const NPI =
      identifiers.find((item: any) => item.type === 'NPI')?.value || '';
    Object.assign(flattened.responsedata, { SSN: ssn, EIN: ein, NPI: NPI });
    console.log('flattened data', flattened);

    return flattened;
  }

  getAddressValidations(permanentAddress: any) {
    // Initialize an empty object for permanentAddressErrors
    const permanentAddressErrors: any = {};

    const { street1, city, state, zipCode, countyOrRegion } = performValidation(
      permanentAddress,
      this.secData,
      'Next', undefined, undefined, this.sectionsService.getFlow(), this.sectionsService.getEnrollmentType()
    );

    // Conditionally add keys to permanentAddressErrors if they have values
    if (street1) {
      permanentAddressErrors.street1 = street1;
    }
    if (city) {
      permanentAddressErrors.city = city;
    }
    if (zipCode) {
      permanentAddressErrors.zipCode = zipCode;
    }
    if (state) {
      permanentAddressErrors.state = state;
    }
    if (countyOrRegion) {
      permanentAddressErrors.countyOrRegion = countyOrRegion;
    }

    return permanentAddressErrors;
  }

  handleCancelSearch() {
    this.mode = Mode.Read;
    this.selectedProviderToAdd.set(null);
    this.disabledTaskManagerCodes = [];
    this.enrollmentDataService.setParticipantAssocChanged(true);
  }

  handleProviderSelected(provider: SearchProvidersDetailsItem) {
    this.selectedProviderToAdd.set(provider);
    this.mode = Mode.Add;
  }

  handleFormUpdate(formGroup: FormGroup) {
    const formGroupValue = formGroup.getRawValue();
    this.infoForm.patchValue({ ...formGroupValue });
  }

  handleAddNewProvider() {
    this.mode = Mode.Add;
  }

  ngOnDestroy(): void {
    this.sectionsService.removeEnrollmentType();
  }
}
